import React from 'react';
import { motion } from 'framer-motion';
import Icon from 'components/AppIcon';
import Button from 'components/ui/Button';

const CTASection = () => {
  return (
    <section className="relative bg-gradient-to-br from-gray-900 via-black to-gray-800 text-white overflow-hidden py-20">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-gradient-to-r from-[#D93B34]/20 via-[#9B8E56]/15 to-amber-600/20"></div>
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGcgZmlsbD0ibm9uZSIgZmlsbC1ydWxlPSJldmVub2RkIj4KPGcgZmlsbD0iIzAwMCIgZmlsbC1vcGFjaXR5PSIwLjEiPgo8cGF0aCBkPSJtMzYgMzQgNi0yIDYgMi02IDIgNi0yeiIvPgo8L2c+CjwvZz4KPHN2Zz4=')] opacity-20"></div>
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center space-y-16">
          {/* Main CTA Content */}
          <motion.div 
            className="space-y-10"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <div className="space-y-6">
              <motion.div 
                className="inline-flex items-center space-x-3 bg-gradient-to-r from-amber-500/20 to-amber-600/20 backdrop-blur-sm rounded-full px-8 py-4 border border-amber-400/30"
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
              >
                <Icon name="Handshake" size={28} className="text-amber-300" />
                <span className="text-xl font-semibold">Rejoignez Notre Histoire</span>
              </motion.div>
              
              <motion.h2 
                className="text-4xl md:text-6xl lg:text-7xl font-bold leading-tight"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                viewport={{ once: true }}
              >
                Écrivons Ensemble
                <span className="block text-transparent bg-gradient-to-r from-amber-300 via-amber-400 to-amber-500 bg-clip-text">
                  le Prochain Chapitre
                </span>
              </motion.h2>
              
              <motion.p 
                className="text-xl md:text-2xl text-gray-200 leading-relaxed max-w-5xl mx-auto"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                viewport={{ once: true }}
              >
                Fort de 18 années d'expertise exceptionnelle et d'une vision modernisée par l'intelligence artificielle, 
                CRF Perfection vous invite à participer à la suite de cette aventure entrepreneuriale unique et transformatrice.
              </motion.p>
            </div>
            
            {/* Value Propositions */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
              <motion.div 
                className="bg-gradient-to-br from-[#D93B34]/10 to-[#D93B34]/20 backdrop-blur-lg rounded-3xl p-8 border border-[#D93B34]/30 hover:transform hover:scale-105 transition-all duration-300"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.5 }}
                viewport={{ once: true }}
              >
                <div className="space-y-6 text-center">
                  <div className="bg-gradient-to-r from-[#D93B34] to-red-600 rounded-full p-5 w-20 h-20 flex items-center justify-center mx-auto shadow-xl">
                    <Icon name="History" size={40} className="text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-white">Héritage Solide & Éprouvé</h3>
                  <p className="text-gray-300 leading-relaxed">
                    18 ans d'expertise technique avérée et de réussites stratégiques partagées 
                    avec nos clients dans leur transformation digitale et organisationnelle.
                  </p>
                </div>
              </motion.div>
              
              <motion.div 
                className="bg-gradient-to-br from-[#9B8E56]/10 to-[#9B8E56]/20 backdrop-blur-lg rounded-3xl p-8 border border-[#9B8E56]/30 hover:transform hover:scale-105 transition-all duration-300"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
                viewport={{ once: true }}
              >
                <div className="space-y-6 text-center">
                  <div className="bg-gradient-to-r from-[#9B8E56] to-amber-600 rounded-full p-5 w-20 h-20 flex items-center justify-center mx-auto shadow-xl">
                    <Icon name="Zap" size={40} className="text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-white">Innovation Continue & IA</h3>
                  <p className="text-gray-300 leading-relaxed">
                    Intégration pionière de l'intelligence artificielle et des technologies émergentes 
                    au cœur de nos services et solutions stratégiques avancées.
                  </p>
                </div>
              </motion.div>
              
              <motion.div 
                className="bg-gradient-to-br from-amber-500/10 to-amber-600/20 backdrop-blur-lg rounded-3xl p-8 border border-amber-400/30 hover:transform hover:scale-105 transition-all duration-300"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.7 }}
                viewport={{ once: true }}
              >
                <div className="space-y-6 text-center">
                  <div className="bg-gradient-to-r from-amber-500 to-amber-600 rounded-full p-5 w-20 h-20 flex items-center justify-center mx-auto shadow-xl">
                    <Icon name="Globe" size={40} className="text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-white">Vision Globale & Internationale</h3>
                  <p className="text-gray-300 leading-relaxed">
                    Présence internationale stratégique avec notre agence de Washington D.C. 
                    et une vision globale des enjeux de transformation des entreprises.
                  </p>
                </div>
              </motion.div>
            </div>
            
            {/* CTA Buttons */}
            <motion.div 
              className="flex flex-col sm:flex-row gap-6 justify-center items-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.8 }}
              viewport={{ once: true }}
            >
              <Button
                size="lg"
                className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white font-bold px-12 py-6 rounded-xl shadow-2xl transition-all duration-300 hover:scale-105 border-0 text-lg"
              >
                Démarrer un Projet Stratégique
                <Icon name="Rocket" size={28} className="ml-3" />
              </Button>
              
              <Button
                variant="outline"
                size="lg"
                className="border-2 border-white text-white hover:bg-white hover:text-gray-900 font-bold px-12 py-6 rounded-xl backdrop-blur-sm bg-white/10 transition-all duration-300 text-lg"
              >
                <Icon name="Calendar" size={28} className="mr-3" />
                Consultation Stratégique Gratuite
              </Button>
            </motion.div>
          </motion.div>
          
          {/* Enhanced Stats Section */}
          <motion.div 
            className="bg-gradient-to-r from-white/5 to-white/10 backdrop-blur-lg rounded-3xl p-12 border border-white/20"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            viewport={{ once: true }}
          >
            <h3 className="text-3xl font-bold text-center text-white mb-12">Notre Impact en Chiffres</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              <div className="text-center space-y-3">
                <div className="text-4xl md:text-5xl font-bold text-transparent bg-gradient-to-r from-[#D93B34] to-red-500 bg-clip-text">2007</div>
                <p className="text-gray-300 font-medium">Année de Création</p>
                <p className="text-gray-400 text-sm">Sinus International</p>
              </div>
              
              <div className="text-center space-y-3">
                <div className="text-4xl md:text-5xl font-bold text-transparent bg-gradient-to-r from-[#9B8E56] to-amber-500 bg-clip-text">18+</div>
                <p className="text-gray-300 font-medium">Années d'Expertise</p>
                <p className="text-gray-400 text-sm">Excellence Continue</p>
              </div>
              
              <div className="text-center space-y-3">
                <div className="text-4xl md:text-5xl font-bold text-transparent bg-gradient-to-r from-amber-400 to-amber-600 bg-clip-text">500+</div>
                <p className="text-gray-300 font-medium">Projets Réalisés</p>
                <p className="text-gray-400 text-sm">Succès Mesurés</p>
              </div>
              
              <div className="text-center space-y-3">
                <div className="text-4xl md:text-5xl font-bold text-transparent bg-gradient-to-r from-purple-400 to-pink-500 bg-clip-text">∞</div>
                <p className="text-gray-300 font-medium">Potentiel Innovation</p>
                <p className="text-gray-400 text-sm">Avenir Illimité</p>
              </div>
            </div>
          </motion.div>
          
          {/* Enhanced Contact Information */}
          <motion.div 
            className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-lg rounded-3xl p-10 border border-gray-700/50 max-w-5xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            viewport={{ once: true }}
          >
            <div className="text-center mb-8">
              <h4 className="text-2xl font-bold text-white mb-2">Commencez Votre Transformation</h4>
              <p className="text-gray-300">Nos experts sont à votre disposition pour discuter de vos projets</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
              <div className="space-y-4">
                <div className="bg-gradient-to-r from-[#D93B34]/20 to-[#D93B34]/10 rounded-full p-4 w-16 h-16 flex items-center justify-center mx-auto">
                  <Icon name="MapPin" size={24} className="text-[#D93B34]" />
                </div>
                <div>
                  <h5 className="font-bold text-white mb-2 text-lg">Siège Social</h5>
                  <p className="text-gray-300">Cotonou, Bénin</p>
                  <p className="text-gray-400 text-sm">Centre Africain d'Excellence</p>
                </div>
              </div>
              
              <div className="space-y-4">
                <div className="bg-gradient-to-r from-[#9B8E56]/20 to-[#9B8E56]/10 rounded-full p-4 w-16 h-16 flex items-center justify-center mx-auto">
                  <Icon name="Globe" size={24} className="text-[#9B8E56]" />
                </div>
                <div>
                  <h5 className="font-bold text-white mb-2 text-lg">Agence Internationale</h5>
                  <p className="text-gray-300">Washington D.C., USA</p>
                  <p className="text-gray-400 text-sm">Hub Technologique</p>
                </div>
              </div>
              
              <div className="space-y-4">
                <div className="bg-gradient-to-r from-amber-500/20 to-amber-600/10 rounded-full p-4 w-16 h-16 flex items-center justify-center mx-auto">
                  <Icon name="MessageSquare" size={24} className="text-amber-500" />
                </div>
                <div>
                  <h5 className="font-bold text-white mb-2 text-lg">Consultation Stratégique</h5>
                  <p className="text-gray-300">100% Gratuite</p>
                  <p className="text-gray-400 text-sm">Évaluation Personnalisée</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
      {/* Enhanced Floating Elements */}
      <div className="absolute top-20 left-10 bg-gradient-to-r from-amber-400 to-amber-500 rounded-2xl p-5 shadow-2xl animate-pulse hidden lg:block">
        <Icon name="Star" size={40} className="text-white" />
      </div>
      <div className="absolute bottom-20 right-10 bg-gradient-to-r from-white to-gray-100 rounded-2xl p-5 shadow-2xl hidden lg:block">
        <div className="flex items-center space-x-3">
          <Icon name="Award" size={32} className="text-[#D93B34]" />
          <div>
            <p className="font-bold text-[#D93B34] text-lg">Excellence</p>
            <p className="text-gray-600 text-sm">Depuis 2007</p>
          </div>
        </div>
      </div>
      {/* Top Wave */}
      <div className="absolute top-0 left-0 right-0 transform rotate-180">
        <svg viewBox="0 0 1200 120" preserveAspectRatio="none" className="w-full h-16 fill-gray-50">
          <path d="M0,0V46.29c47.79,22.2,103.59,32.17,158,28,70.36-5.37,136.33-33.31,206.8-37.5C438.64,32.43,512.34,53.67,583,72.05c69.27,18,138.3,24.88,209.4,13.08,36.15-6,69.85-17.84,104.45-29.34C989.49,25,1113-14.29,1200,52.47V0Z" opacity=".25"></path>
          <path d="M0,0V15.81C13,36.92,27.64,56.86,47.69,72.05,99.41,111.27,165,111,224.58,91.58c31.15-10.15,60.09-26.07,89.67-39.8,40.92-19,84.73-46,130.83-49.67,36.26-2.85,70.9,9.42,98.6,31.56,31.77,25.39,62.32,62,103.63,73,40.44,10.79,81.35-6.69,119.13-24.28s75.16-39,116.92-43.05c59.73-5.85,113.28,22.88,168.9,38.84,30.2,8.66,59,6.17,87.09-7.5,22.43-10.89,48-26.93,60.65-49.24V0Z" opacity=".5"></path>
          <path d="M0,0V5.63C149.93,59,314.09,71.32,475.83,42.57c43-7.64,84.23-20.12,127.61-26.46,59-8.63,112.48,12.24,165.56,35.4C827.93,77.22,886,95.24,951.2,90c86.53-7,172.46-45.71,248.8-84.81V0Z"></path>
        </svg>
      </div>
    </section>
  );
};

export default CTASection;